import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Table, Tag, Space, Alert, Tabs, message } from "antd";
import { ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import { ModalItem, ButtonItem } from "../../../../Components/Items";
import { fmtMXN } from "./aguinaldoUtils";

const codigoTexto = (c) => (c === null || c === undefined ? "—" : String(c).padStart(4, "0"));

/**
 * "Resetear líneas" del borrador de aguinaldo: compara las filas del borrador contra el padrón
 * elegible de hoy y deja elegir quién sale (baja, deshabilitado, sin cálculo de aguinaldo...) y
 * quién entra (altas y reingresos). Los que se quedan no se tocan.
 *
 * Es la versión corta de PreNomina/ModalResetearLineas: el servidor decide los motivos y qué viene
 * palomeado (nomina/previsualizarResetAguinaldo), y al aplicar vuelve a armar el diff y sólo
 * ejecuta lo que siga siendo cierto (nomina/aplicarResetAguinaldo).
 */
class ModalResetearAguinaldo extends Component {
  state = { cargando: false, aplicando: false, preview: null, error: null, quitarSel: [], agregarSel: [] };

  componentDidMount() {
    if (this.props.visible) this.cargarPreview();
  }

  componentDidUpdate(prev) {
    if (this.props.visible && !prev.visible) this.cargarPreview();
  }

  post(url, body) {
    const { empresaId, headersToken } = this.props;
    return axios.post(url, { empresaId, ...body }, { headers: headersToken });
  }

  async cargarPreview() {
    this.setState({ cargando: true, error: null, preview: null });
    try {
      const res = await this.post("nomina/previsualizarResetAguinaldo", { id: this.props.procesoId });
      if (res.data.success === 2) {
        this.setState({
          preview: res.data,
          quitarSel: res.data.aQuitar.filter((r) => r.preseleccionada).map((r) => r.empleadoId),
          agregarSel: res.data.aAgregar.filter((r) => r.preseleccionada).map((r) => r.empleadoId),
        });
      } else {
        this.setState({ error: res.data.message || "No se pudo comparar contra el padrón" });
      }
    } catch {
      this.setState({ error: "Error de conexión al comparar contra el padrón" });
    }
    this.setState({ cargando: false });
  }

  aplicar = async () => {
    const { quitarSel, agregarSel } = this.state;
    this.setState({ aplicando: true });
    try {
      const res = await this.post("nomina/aplicarResetAguinaldo", {
        id: this.props.procesoId,
        quitarEmpleadoIds: quitarSel,
        agregarEmpleadoIds: agregarSel,
      });
      if (res.data.success === 2) {
        const { quitados, agregados, omitidos } = res.data.resumen;
        const texto = `Borrador actualizado: ${quitados} quitado(s), ${agregados} agregado(s).`;
        if (omitidos) message.warning(`${texto} ${omitidos} ya no aplicaban y se omitieron.`);
        else message.success(texto);
        this.props.onAplicado(res.data.proceso);
      } else {
        message.error(res.data.message || "No se pudo resetear el borrador");
      }
    } catch {
      message.error("Error de conexión al resetear el borrador");
    }
    this.setState({ aplicando: false });
  };

  columnas(conAviso) {
    return [
      { title: "No.", dataIndex: "codigo", width: 70, render: codigoTexto },
      {
        title: "Empleado",
        dataIndex: "nombre",
        render: (nombre, r) => (
          <div>
            <div>{nombre || "—"}</div>
            {r.editado && <Tag style={{ marginTop: 2 }}>Editado a mano</Tag>}
            {conAviso && r.aviso && <div className="aguinaldo-aviso-texto">{r.aviso}</div>}
          </div>
        ),
      },
      { title: "Motivo", dataIndex: "motivo", width: 300 },
      {
        title: "Importe",
        dataIndex: "importe",
        width: 120,
        align: "right",
        render: (v, r) => <span className={r.incluido ? "" : "aguinaldo-tenue"}>{fmtMXN(v)}</span>,
      },
    ];
  }

  render() {
    const { visible, onCerrar } = this.props;
    const { cargando, aplicando, preview, error, quitarSel, agregarSel } = this.state;
    const nadaSeleccionado = !quitarSel.length && !agregarSel.length;

    return (
      <ModalItem
        title="Resetear líneas del borrador"
        visible={visible}
        spinning={cargando}
        onCancel={onCerrar}
        footer={null}
        width={900}
      >
        {error ? (
          <div style={{ paddingTop: 8 }}>
            <Alert type="error" showIcon message={error} />
            <div style={{ textAlign: "right", marginTop: 16 }}>
              <Space>
                <ButtonItem onClick={onCerrar}>Cerrar</ButtonItem>
                <ButtonItem icon={<ReloadOutlined />} onClick={() => this.cargarPreview()}>Reintentar</ButtonItem>
              </Space>
            </div>
          </div>
        ) : !preview ? null : (
          <div style={{ paddingTop: 8 }}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 10 }}
              message="Se compara con los empleados a los que hoy les toca aguinaldo al corte."
              description="Los que siguen no se tocan: conservan sus días, importes, ediciones e ISR calculado."
            />
            {preview.avisos.map((a) => (
              <Alert key={a} type="warning" showIcon style={{ marginBottom: 10 }} message={a} />
            ))}

            <Space wrap style={{ marginBottom: 10 }}>
              <Tag color="error">{preview.aQuitar.length} se pueden quitar</Tag>
              <Tag color="success">{preview.aAgregar.length} se pueden agregar</Tag>
              <Tag>{preview.conservadas} sin cambios</Tag>
            </Space>

            <Tabs size="small" defaultActiveKey={preview.aQuitar.length || !preview.aAgregar.length ? "quitar" : "agregar"}>
              <Tabs.TabPane tab={`Se quitan (${quitarSel.length}/${preview.aQuitar.length})`} key="quitar">
                <Table
                  size="small"
                  rowKey="empleadoId"
                  columns={this.columnas(false)}
                  dataSource={preview.aQuitar}
                  pagination={{ pageSize: 8, showSizeChanger: false, size: "small", hideOnSinglePage: true }}
                  locale={{ emptyText: "Nadie sobra: todos los del borrador siguen teniendo aguinaldo." }}
                  rowSelection={{ selectedRowKeys: quitarSel, onChange: (keys) => this.setState({ quitarSel: keys }) }}
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab={`Se agregan (${agregarSel.length}/${preview.aAgregar.length})`} key="agregar">
                <Table
                  size="small"
                  rowKey="empleadoId"
                  columns={this.columnas(true)}
                  dataSource={preview.aAgregar}
                  pagination={{ pageSize: 8, showSizeChanger: false, size: "small", hideOnSinglePage: true }}
                  locale={{ emptyText: "No falta nadie: no hay altas ni reingresos fuera del borrador." }}
                  rowSelection={{ selectedRowKeys: agregarSel, onChange: (keys) => this.setState({ agregarSel: keys }) }}
                />
              </Tabs.TabPane>
            </Tabs>

            <div className="aguinaldo-pie">
              <span className="aguinaldo-tenue">
                El borrador quedaría con{" "}
                <b>{preview.conservadas + (preview.aQuitar.length - quitarSel.length) + agregarSel.length}</b> empleado(s).
              </span>
              <Space>
                <ButtonItem onClick={onCerrar}>Cancelar</ButtonItem>
                <ButtonItem icon={<ReloadOutlined />} onClick={() => this.cargarPreview()} disabled={aplicando}>
                  Volver a comparar
                </ButtonItem>
                <ButtonItem
                  type="primary"
                  icon={<SyncOutlined />}
                  disabled={nadaSeleccionado}
                  loading={aplicando}
                  onClick={this.aplicar}
                >
                  Aplicar — quitar {quitarSel.length}, agregar {agregarSel.length}
                </ButtonItem>
              </Space>
            </div>
          </div>
        )}
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalResetearAguinaldo);
